import React from 'react';
import { _cs } from '@togglecorp/fujs';
import { Link, LinkProps } from '@reach/router';

import { pathNames } from '#constants';

import Button from '#rsca/Button';
import FloatingContainer from '#rscv/FloatingContainer';

import DisplayPicture from '#components/DisplayPicture';
import AuthContext from '#components/AuthContext';

import * as types from '#constants/types';

import styles from './styles.scss';

interface Props {
    className?: string;
    closeModal: () => void;
    userDetail?: types.UserDetail;
}

interface State {
    closeNavDrawer: boolean;
}

interface NavLinkProps extends Omit<LinkProps<any>, 'ref'> {
    className?: string;
    activeClassName?: string;
}

function NavLink(props: NavLinkProps) {
    const {
        className: classNameFromProps,
        activeClassName,
        ...otherProps
    } = props;

    return (
        <Link
            {...otherProps}
            getProps={({ isCurrent }) => ({
                className: isCurrent
                    ? _cs(classNameFromProps, activeClassName)
                    : classNameFromProps,
            })}
        />
    );
}

class NavDrawer extends React.PureComponent<Props, State> {
    public constructor(props: Props) {
        super(props);

        // NOTE: A history is created so as to make back button click close
        // the nav drawer
        window.history.pushState({}, '');

        this.state = {
            closeNavDrawer: false,
        };
    }

    public componentDidMount() {
        // NOTE: This listens for back button click
        window.addEventListener('popstate', this.closeFromBackButton);
    }

    public componentWillUnmount() {
        window.removeEventListener('popstate', this.closeFromBackButton);
    }

    public context!: React.ContextType<typeof AuthContext>;

    private closeFromBackButton = () => {
        this.handleClose(true);
    }

    private handleCloseFromButtonClick = () => {
        this.handleClose(false);
    }

    private handleCloseFromBlurClick = () => {
        this.handleClose(false);
    }

    private handleClose = (fromBackButton: boolean = false) => {
        // NOTE: Timeout is required for animation of navdrawer
        this.setState({ closeNavDrawer: true }, () => {
            setTimeout(() => {
                this.props.closeModal();
                if (!fromBackButton) {
                    // NOTE: If drawer is closed by outside click, it should remove
                    // the added history from constructor
                    window.history.go(-1);
                }
            }, 200);
        });
    };

    public render() {
        const {
            className,
            userDetail,
        } = this.props;

        const { closeNavDrawer } = this.state;
        const { logout } = this.context;

        const emptyUserDetail = {
            display_name: 'Anonymous',
            email: undefined,
            username: undefined,
        };

        const {
            display_name,
            email,
            username,
        } = userDetail || emptyUserDetail;

        return (
            <FloatingContainer
                className={_cs(
                    className,
                    styles.navDrawer,
                    closeNavDrawer && styles.closeNavDrawer,
                )}
                showHaze
                onBlur={this.handleCloseFromBlurClick}
            >
                <header className={styles.header}>
                    <div className={styles.top}>
                        <h3 className={styles.heading}>
                            Golden Community
                        </h3>
                        <Button
                            onClick={this.handleCloseFromButtonClick}
                            className={styles.closeButton}
                            transparent
                            iconName="close"
                        />
                    </div>
                    <div className={styles.userDetails}>
                        <DisplayPicture
                            className={styles.displayPicture}
                            altText={display_name}
                        />
                        <div className={styles.details}>
                            <div className={styles.name}>
                                { display_name }
                            </div>
                            {username && (
                                <div className={styles.username}>
                                    { username }
                                </div>
                            )}
                            {email && (
                                <div className={styles.email}>
                                    { email }
                                </div>
                            )}
                        </div>
                    </div>
                </header>
                <div className={styles.content}>
                    <NavLink
                        activeClassName={styles.active}
                        className={styles.link}
                        to={pathNames.projects}
                    >
                        Projects
                    </NavLink>
                    <NavLink
                        activeClassName={styles.active}
                        className={styles.link}
                        to={pathNames.patients}
                    >
                        Patients
                    </NavLink>
                    <NavLink
                        activeClassName={styles.active}
                        className={styles.link}
                        to={pathNames.hospitals}
                    >
                        Hospitals
                    </NavLink>
                    <hr className={styles.separator} />
                    <Button
                        className={styles.logout}
                        transparent
                        onClick={logout}
                    >
                        Logout
                    </Button>
                </div>
                <div className={styles.footer}>
                    <div className={styles.version}>
                        {process.env.REACT_APP_VERSION}
                    </div>
                </div>
            </FloatingContainer>
        );
    }
}

NavDrawer.contextType = AuthContext;
export default NavDrawer;
